import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { titleCase } from '../lib/titleCase'
import { Icons } from '../icons/registry'
import { IconButton } from './IconButton'
import { ConfirmDialog, type ConfirmQuestion } from './ConfirmDialog'

export interface ActionMenuItem {
  key: string
  label: string
  /** A small mark before the words — a status move shows where it lands. */
  icon?: ReactNode
  /** Rendered, but not activatable while the work is in flight. */
  disabled?: boolean
  /** Destructive rows ink danger and ask twice before firing. */
  danger?: boolean
  /** The armed row's words. Defaults to `Confirm ${label}`. */
  confirmLabel?: string
  /**
   * An ending that cannot be taken back asks in a window instead of arming
   * the row: the menu closes and the question takes its place.
   */
  ask?: ConfirmQuestion
  onSelect: () => void
}

export interface ActionMenuSection {
  /** Small caps over the section. Omitted for a single undivided list. */
  title?: string
  items: ActionMenuItem[]
}

/**
 * The overflow every crowded header funnels into: one trigger, one solid
 * panel, the verbs in labelled sections. What the hand reaches for
 * constantly stays out as a button; everything else — status moves, copies,
 * archives, the endings — waits here, one click away.
 *
 * Two triggers: `dots` for rows, where a label per row would bloat the
 * list, and `labeled` for headers, where the dots hide too much — a pill
 * that says Actions, its chevron turning as the panel arrives. Destructive
 * rows arm on the first press and fire on the second; closing the panel
 * disarms them. A third, custom trigger lets a row's own mark open the
 * panel — the job row's status circle — aligned to whichever edge it sits
 * on. A row with an `ask` opens a confirm window instead. Selecting any row closes the panel — refusals speak through
 * the console-wide toast, never from inside the menu.
 */
export function ActionMenu({
  label,
  sections,
  trigger = 'dots',
  align = 'end',
  className,
}: {
  /** Names the trigger for screen readers and its tooltip. */
  label: string
  sections: readonly ActionMenuSection[]
  trigger?: 'dots' | 'labeled' | { content: ReactNode; className?: string }
  /** Which edge of the trigger the panel hangs from. */
  align?: 'start' | 'end'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [armedKey, setArmedKey] = useState<string | null>(null)
  const [asking, setAsking] = useState<ActionMenuItem | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const close = () => {
    setOpen(false)
    setArmedKey(null)
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open ])

  const itemClass = (danger?: boolean) =>
    cn(
      'flex w-full items-center rounded-lg px-3 py-2 text-left text-ui transition-colors duration-fast outline-none',
      'hover:bg-tint focus-visible:bg-tint disabled:pointer-events-none disabled:opacity-45',
      danger ? 'text-danger' : 'text-foreground',
    )

  return (
    <div ref={rootRef} className={cn('relative shrink-0', className)}>
      {trigger === 'labeled' ? (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          onClick={() => (open ? close() : setOpen(true))}
          className="inline-flex items-center gap-2 rounded-full border border-hairline py-[7px] pr-3.5 pl-3 text-ui font-medium text-foreground transition-colors duration-fast outline-none hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground"
        >
          <Icons.settings size="text" className="text-muted-foreground" />
          Actions
          {/* A flex box with a centered origin: transforms on inline boxes
              rotate around whatever the browser guesses, and the chevron
              visibly jumps. This one turns in place. */}
          <span className={cn('inline-flex origin-center text-muted-foreground transition-transform duration-fast', open && 'rotate-180')}>
            <Icons.dropdown />
          </span>
        </button>
      ) : typeof trigger === 'object' ? (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          onClick={() => (open ? close() : setOpen(true))}
          className={trigger.className}
        >
          {trigger.content}
        </button>
      ) : (
        <IconButton
          ref={triggerRef}
          label={label}
          tooltipSide="bottom"
          onClick={() => (open ? close() : setOpen(true))}
        >
          <span className="rotate-90">
            <Icons.more />
          </span>
        </IconButton>
      )}
      {open ? (
        <div
          role="menu"
          aria-label={label}
          className={cn('absolute top-[calc(100%+4px)] z-30 w-56', align === 'end' ? 'right-0' : 'left-0', ' rounded-xl border border-hairline bg-surface p-1 shadow-overlay')}
        >
          {sections.map((section, index) => (
            <div key={section.title ?? `section-${index}`} className={cn(index > 0 && 'mt-1 border-t border-hairline pt-1')}>
              {section.title ? (
                <p className="px-3 pt-2 pb-1 text-micro text-muted-foreground uppercase">{section.title}</p>
              ) : null}
              {section.items.map((item) => {
                const armed = armedKey === item.key
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                      if (item.ask) {
                        close()
                        setAsking(item)
                        return
                      }
                      if (item.danger && !armed) {
                        setArmedKey(item.key)
                        return
                      }
                      close()
                      item.onSelect()
                    }}
                    className={itemClass(item.danger)}
                  >
                    {item.icon ? <span aria-hidden className={cn('mr-2.5 flex shrink-0', item.danger ? 'text-danger [&_.wf-icon]:text-danger' : 'text-muted-foreground')}>{item.icon}</span> : null}
                    {titleCase(armed ? (item.confirmLabel ?? `Confirm ${item.label.toLowerCase()}`) : item.label)}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      ) : null}
      {asking?.ask ? (
        <ConfirmDialog open question={asking.ask} onConfirm={asking.onSelect} onClose={() => setAsking(null)} />
      ) : null}
    </div>
  )
}
